
import React, { useState, useRef, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import ReducedContent from './ReducedContent';
import { Modal, Button, Alert, Form, Row, Col, Card } from 'react-bootstrap';
import { uploadMultipartForm, fetchOptions } from '../../api/api';
import { useNavigate } from 'react-router-dom';

const FileRow = ({
  file,
  index,
  individualValues,
  onChange,
  subjects
}) => {
  const [grades, setGrades] = useState([]);
  const [categories, setCategories] = useState([]);

  // Use individual values directly as they are populated from global in Step 1 transition
  const effectiveMateria = individualValues.materia;
  const effectiveAno = individualValues.ano;

  // Fetch Grades when Materia changes
  useEffect(() => {
    const fetchGrades = async () => {
      if (!effectiveMateria) {
        setGrades([]);
        return;
      }
      try {
        const options = await fetchOptions(effectiveMateria, null);
        setGrades(options.grades || []);
      } catch (error) {
        console.error("Error fetching grades", error);
      }
    };
    fetchGrades();
  }, [effectiveMateria]);

  // Fetch Categories when Grade (and Materia) changes
  useEffect(() => {
    const fetchCategories = async () => {
      if (!effectiveAno || !effectiveMateria) {
        setCategories([]);
        return;
      }
      try {
        const options = await fetchOptions(effectiveMateria, effectiveAno);
        setCategories(options.categories || []);
      } catch (error) {
        console.error("Error fetching categories", error);
      }
    };
    fetchCategories();
  }, [effectiveMateria, effectiveAno]);

  const handleChange = (field, value) => {
    onChange(index, field, value);
  };

  return (
    <Card className="mb-3">
      <Card.Body>
        <Row>
          <Col md={2} className="d-flex align-items-center justify-content-center bg-light">
            {file.type === 'application/pdf' ? (
              <div className="text-center">
                <strong>PDF</strong>
                <div className="small text-muted text-truncate" style={{ maxWidth: '100px' }}>{file.name}</div>
              </div>
            ) : (
              <img
                src={URL.createObjectURL(file)}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'contain' }}
              />
            )}
          </Col>
          <Col md={10}>
            <Row className="g-2">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small">Matéria</Form.Label>
                  <Form.Select
                    value={individualValues.materia}
                    onChange={(e) => handleChange('materia', e.target.value)}
                    size="sm"
                  >
                    <option value="">Selecione...</option>
                    {subjects.map(s => <option key={s.uuid} value={s.uuid}>{s.name}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small">Ano</Form.Label>
                  <Form.Select
                    value={individualValues.ano}
                    onChange={(e) => handleChange('ano', e.target.value)}
                    disabled={!effectiveMateria}
                    size="sm"
                  >
                    <option value="">Selecione...</option>
                    {grades.map(g => <option key={g.uuid} value={g.uuid}>{g.name}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small">Categoria</Form.Label>
                  <Form.Select
                    value={individualValues.categoria}
                    onChange={(e) => handleChange('categoria', e.target.value)}
                    disabled={!effectiveAno}
                    size="sm"
                  >
                    <option value="">Selecione...</option>
                    {categories.map(c => <option key={c.uuid} value={c.uuid}>{c.name}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">Título</Form.Label>
                  <Form.Control
                    type="text"
                    value={individualValues.titulo}
                    onChange={(e) => handleChange('titulo', e.target.value)}
                    size="sm"
                    placeholder={file.name}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small">Dificuldade</Form.Label>
                  <Form.Select
                    value={individualValues.dificuldade}
                    onChange={(e) => handleChange('dificuldade', e.target.value)}
                    size="sm"
                  >
                    <option value="">Selecione...</option>
                    <option value="1">Fácil</option>
                    <option value="2">Médio</option>
                    <option value="3">Difícil</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small">Descrição</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={individualValues.descricao}
                    onChange={(e) => handleChange('descricao', e.target.value)}
                    size="sm"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};


const ActivityAdd2 = () => {
  const [step, setStep] = useState(1);
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Required fields tracking
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  // Global State
  const [globalEnabled, setGlobalEnabled] = useState({
    materia: false,
    ano: false,
    categoria: false,
    titulo: false,
    descricao: false,
    dificuldade: false
  });

  const [globalValues, setGlobalValues] = useState({
    materia: '',
    ano: '',
    categoria: '',
    titulo: '',
    descricao: '',
    dificuldade: ''
  });

  // Individual State
  const [fileForms, setFileForms] = useState([]);

  // Data Options
  const [subjects, setSubjects] = useState([]);
  const [globalGrades, setGlobalGrades] = useState([]);
  const [globalCategories, setGlobalCategories] = useState([]);

  const navigate = useNavigate();

  // Load Subjects on Mount
  useEffect(() => {
    const loadSubjects = async () => {
      try {
        const options = await fetchOptions(null, null);
        setSubjects(options.subjects || []);
      } catch (error) {
        console.error("Failed to load subjects", error);
      }
    };
    loadSubjects();
  }, []);

  // Handle Global Dependency Updates
  useEffect(() => {
    const updateGlobalGrades = async () => {
      if (globalEnabled.materia && globalValues.materia) {
        try {
          const options = await fetchOptions(globalValues.materia, null);
          setGlobalGrades(options.grades || []);
        } catch (e) {
          setGlobalGrades([]);
        }
      } else {
        setGlobalGrades([]);
      }
    };
    updateGlobalGrades();
  }, [globalValues.materia, globalEnabled.materia]);

  useEffect(() => {
    const updateGlobalCategories = async () => {
      if (globalEnabled.ano && globalValues.ano && globalValues.materia) {
        try {
          const options = await fetchOptions(globalValues.materia, globalValues.ano);
          setGlobalCategories(options.categories || []);
        } catch (e) {
          setGlobalCategories([]);
        }
      } else {
        setGlobalCategories([]);
      }
    };
    updateGlobalCategories();
  }, [globalValues.ano, globalValues.materia, globalEnabled.ano]);


  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
      setFileForms(files.map(f => ({
        materia: '',
        ano: '',
        categoria: '',
        dificuldade: '1',
        titulo: f.name.split('.').slice(0, -1).join('.'), // Default title from filename
        descricao: ''
      })));
    }
  };

  const toggleGlobal = (field) => {
    setGlobalEnabled(prev => {
      const newState = { ...prev, [field]: !prev[field] };
      if (field === 'ano' && newState.ano) newState.materia = true;
      if (field === 'categoria' && newState.categoria) {
        newState.ano = true;
        newState.materia = true;
      }
      if (field === 'materia' && !newState.materia) {
        newState.ano = false;
        newState.categoria = false;
      }
      if (field === 'ano' && !newState.ano) {
        newState.categoria = false;
      }
      return newState;
    });
  };

  const handleGlobalChange = (field, value) => {
    setGlobalValues(prev => ({ ...prev, [field]: value }));
  };

  const handleIndividualChange = (index, field, value) => {
    setFileForms(prev => {
      const newForms = [...prev];
      newForms[index] = { ...newForms[index], [field]: value };
      return newForms;
    });
  };

  const handleNextStep = () => {
    setFileForms(prevForms => prevForms.map(form => ({
      ...form,
      materia: globalEnabled.materia ? globalValues.materia : form.materia,
      ano: globalEnabled.ano ? globalValues.ano : form.ano,
      categoria: globalEnabled.categoria ? globalValues.categoria : form.categoria,
      titulo: globalEnabled.titulo ? globalValues.titulo : form.titulo,
      descricao: globalEnabled.descricao ? globalValues.descricao : form.descricao,
      dificuldade: globalEnabled.dificuldade ? globalValues.dificuldade : form.dificuldade,
    })));
    setStep(2);
  };

  const validateStep2 = () => {
    for (const form of fileForms) {
      if (!form.materia || !form.ano || !form.categoria || !form.titulo || !form.descricao || !form.dificuldade) {
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep2()) {
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 3000);
      return;
    }

    setIsButtonDisabled(true);
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const formData = new FormData();
        const form = fileForms[i];

        formData.append('file', selectedFiles[i]);
        formData.append('subject_uuid', form.materia);
        formData.append('year', form.ano);
        formData.append('category_uuid', form.categoria);
        formData.append('difficulty', form.dificuldade);
        formData.append('description', form.descricao);
        formData.append('title', form.titulo);

        await uploadMultipartForm(formData);
      }
      setShowSuccessAlert(true);
      setTimeout(() => {
        setShowSuccessAlert(false);
        setStep(1);
        setSelectedFiles([]);
        setFileForms([]);
        setGlobalValues({ materia: '', ano: '', categoria: '', titulo: '', descricao: '', dificuldade: '' });
        setGlobalEnabled({ materia: false, ano: false, categoria: false, titulo: false, descricao: false, dificuldade: false });
      }, 2000);

    } catch (e) {
      console.error(e);
      setShowErrorAlert(true);
    }
    setIsButtonDisabled(false);
  };

  return (
    <DashboardLayout title="Adicionar Atividades (Multi-upload)">
      <ReducedContent>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Button variant="link" onClick={() => navigate("/dashboard/activity/add")}>Upload simples</Button>
          {step === 2 && <Button variant="secondary" size="sm" onClick={() => setStep(1)}>Voltar para Seleção</Button>}
        </div>

        {showSuccessAlert && <Alert variant="success">Atividades enviadas com sucesso!</Alert>}
        {showErrorAlert && <Alert variant="danger">Por favor, preencha todos os campos obrigatórios.</Alert>}

        {step === 1 && (
          <Card className="p-4 shadow-sm">
            <h5 className="mb-4">Passo 1: Seleção de Arquivos e Dados Comuns</h5>

            <Form.Group className="mb-4">
              <Form.Label>Selecione os Arquivos</Form.Label>
              <Form.Control
                type="file"
                multiple
                onChange={handleFileChange}
                ref={(ref) => ref && (ref.value = "")}
              />
              <Form.Text className="text-muted">
                {selectedFiles.length > 0 ? `${selectedFiles.length} arquivos selecionados.` : 'Nenhum arquivo selecionado.'}
              </Form.Text>
            </Form.Group>

            {selectedFiles.length > 0 && (
              <div className="mb-4">
                <h6>Definir padrão para todos os arquivos:</h6>
                <div className="d-flex gap-2 flex-wrap mb-3">
                  {['materia', 'ano', 'categoria', 'titulo', 'descricao', 'dificuldade'].map(field => (
                    <Button
                      key={field}
                      variant={globalEnabled[field] ? "primary" : "outline-secondary"}
                      size="sm"
                      onClick={() => toggleGlobal(field)}
                      className="text-capitalize"
                    >
                      {field} {globalEnabled[field] ? '✓' : '+'}
                    </Button>
                  ))}
                </div>

                <Row className="g-3">
                  {globalEnabled.materia && (
                    <Col md={4}>
                      <Form.Label>Matéria Padrão</Form.Label>
                      <Form.Select
                        value={globalValues.materia}
                        onChange={(e) => handleGlobalChange('materia', e.target.value)}
                      >
                        <option value="">Selecione...</option>
                        {subjects.map(s => <option key={s.uuid} value={s.uuid}>{s.name}</option>)}
                      </Form.Select>
                    </Col>
                  )}
                  {globalEnabled.ano && (
                    <Col md={4}>
                      <Form.Label>Ano Padrão</Form.Label>
                      <Form.Select
                        value={globalValues.ano}
                        onChange={(e) => handleGlobalChange('ano', e.target.value)}
                        disabled={!globalValues.materia}
                      >
                        <option value="">Selecione...</option>
                        {globalGrades.map(g => <option key={g.uuid} value={g.uuid}>{g.name}</option>)}
                      </Form.Select>
                    </Col>
                  )}
                  {globalEnabled.categoria && (
                    <Col md={4}>
                      <Form.Label>Categoria Padrão</Form.Label>
                      <Form.Select
                        value={globalValues.categoria}
                        onChange={(e) => handleGlobalChange('categoria', e.target.value)}
                        disabled={!globalValues.ano}
                      >
                        <option value="">Selecione...</option>
                        {globalCategories.map(c => <option key={c.uuid} value={c.uuid}>{c.name}</option>)}
                      </Form.Select>
                    </Col>
                  )}
                  {globalEnabled.titulo && (
                    <Col md={12}>
                      <Form.Label>Título Padrão</Form.Label>
                      <Form.Control
                        type="text"
                        value={globalValues.titulo}
                        onChange={(e) => handleGlobalChange('titulo', e.target.value)}
                      />
                    </Col>
                  )}
                  {globalEnabled.descricao && (
                    <Col md={12}>
                      <Form.Label>Descrição Padrão</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={globalValues.descricao}
                        onChange={(e) => handleGlobalChange('descricao', e.target.value)}
                      />
                    </Col>
                  )}
                  {globalEnabled.dificuldade && (
                    <Col md={4}>
                      <Form.Label>Dificuldade Padrão</Form.Label>
                      <Form.Select
                        value={globalValues.dificuldade}
                        onChange={(e) => handleGlobalChange('dificuldade', e.target.value)}
                      >
                        <option value="">Selecione...</option>
                        <option value="1">Fácil</option>
                        <option value="2">Médio</option>
                        <option value="3">Difícil</option>
                      </Form.Select>
                    </Col>
                  )}
                </Row>
              </div>
            )}

            <div className="d-flex justify-content-end mt-4">
              <Button
                disabled={selectedFiles.length === 0}
                onClick={handleNextStep}
              >
                Continuar &rarr;
              </Button>
            </div>
          </Card>
        )}

        {step === 2 && (
          <div>
            <h5 className="mb-4">Passo 2: Preencher Detalhes Individuais</h5>
            <p className="text-muted small">Verifique e preencha os detalhes para cada arquivo.</p>

            {selectedFiles.map((file, index) => (
              <FileRow
                key={index}
                index={index}
                file={file}
                individualValues={fileForms[index]}
                onChange={handleIndividualChange}
                subjects={subjects}
              />
            ))}

            <div className="d-flex justify-content-end mt-4">
              <Button variant="success" onClick={handleSubmit} disabled={isButtonDisabled}>
                {isButtonDisabled ? "Enviando..." : "Finalizar Upload"}
              </Button>
            </div>
          </div>
        )}

      </ReducedContent>
    </DashboardLayout>
  );
};

export default ActivityAdd2;
